import * as React from 'react';

import * as PropTypes from 'prop-types';
import { ITranslationContext } from '../../../translations';
import moment from 'moment';
import { Product } from '../../../store/pages/products/types';
import { DateFormat } from "../../../store/pages/venues/types";
import { formatMoment } from "../../../utils/util";
import { ProductStockLevel } from '../../../store/pages/productStock/types';

interface ProductStockLevelTableProps {
    dateFormat: DateFormat;
    productStockLevel: ProductStockLevel;
    product: Product;
}

const ProductStockLevelTable = (props: ProductStockLevelTableProps, context: ITranslationContext) => {
    const { t } = context;
    const { dateFormat, productStockLevel, product } = props;

    if (!productStockLevel) return null;
    const productQuantities = productStockLevel.quantities;

    if (!productQuantities) return null;

    const getStockWarnings = (confirmedStock: number, availableStock: number) => {
        const { lowStockWarningQuantity, oversellQuantityAllowed } = product;

        const warnings: string[] = [];

        const safeLowStockWarningQuantity = lowStockWarningQuantity ?? 0;
        if ((confirmedStock < safeLowStockWarningQuantity) || availableStock < safeLowStockWarningQuantity) {
            // Low Stock
            warnings.push(t('ProductStockLevelTable:warningLowStock').replace('{lowStockWarningQuantity}', safeLowStockWarningQuantity.toString()));
        }

        // Oversell
        const safeOversellQuantityAllowed = -(oversellQuantityAllowed ?? 0);
        if (oversellQuantityAllowed != 0) {
            if (confirmedStock < safeOversellQuantityAllowed || availableStock < safeOversellQuantityAllowed) {
                // Exceeded oversell
                warnings.push(t('ProductStockLevelTable:warningExceededOversell').replace('{oversellQuantityAllowed}', safeOversellQuantityAllowed.toString()));
            }
            else if (confirmedStock < 0 || availableStock < 0) {
                // Into oversell
                warnings.push(t('ProductStockLevelTable:warningInOversell').replace('{oversellQuantityAllowed}', safeOversellQuantityAllowed.toString()));
            }
        }

        return warnings;
    };

    const enhancedData = productQuantities.map((day, index) => {
        const { currentStock, availableStock, paidAllocatedStock, unpaidAllocatedStock } = day;

        const date = moment(day.dateInLocalTime);
        const stockWarnings = date.isSameOrAfter(moment(), 'day')
            ? getStockWarnings(currentStock, availableStock)
            : [];

        return {
            ...day,
            date: date,
            currentStock,
            availableStock,
            paidAllocatedStock,
            unpaidAllocatedStock,
            stockWarnings
        };
    });

    const today = enhancedData.find(day => moment().isSame(day.date, 'day'));
    const tomorrow = enhancedData.find(day => moment().add(1, 'day').isSame(day.date, 'day'));
    const warningIconStyle = { color: '#f9b745', marginRight: '6px' };
    const countStyle = { fontSize: '24px' }

    return (
        <div style={{ flex: '1 1 100%', display:'flex', flexDirection: 'column', alignItems: 'center'}}>
            <div style={{ flex: '1 1 100%', display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: '1rem', margin: '10px 15px' }}>
                {today && (
                    <>
                        <div className='panel panel-primary text-center'>
                            <div className="panel-heading" style={{ minHeight: '62px' }}><h4 className='no-margin'>{t('ProductStockLevelTable:currentStockToday')}</h4></div>
                            <div className="panel-body" style={countStyle}>{today.currentStock}</div>
                        </div>
                        <div className='panel panel-primary text-center'>
                            <div className="panel-heading" style={{ minHeight: '62px' }}>
                                <h4 className='no-margin'>{t('ProductStockLevelTable:allocatedStockToday')}</h4>
                                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <h4 className='no-margin'>{t('ProductStockLevelTable:paid')}</h4>
                                    <h4 className='no-margin'>{t('ProductStockLevelTable:unpaid')}</h4>
                                </div>
                            </div>
                            <div className="panel-body" style={countStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <div>{today.paidAllocatedStock}</div>
                                    <div>{today.unpaidAllocatedStock}</div>
                                </div>       
                            </div>       
                        </div>
                    </>
                )}
                {tomorrow && (
                    <>
                        <div className='panel panel-primary text-center'>
                            <div className="panel-heading" style={{ minHeight: '62px'}}>
                                <h4 className='no-margin'>{t('ProductStockLevelTable:allocatedStockTomorrow')}</h4>
                                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <h4 className='no-margin'>{t('ProductStockLevelTable:paid')}</h4>
                                    <h4 className='no-margin'>{t('ProductStockLevelTable:unpaid')}</h4>
                                </div>
                            </div>
                            <div className="panel-body" style={countStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <div>{tomorrow.paidAllocatedStock}</div>
                                    <div>{tomorrow.unpaidAllocatedStock}</div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <table className='table table-striped table-sm table-condensed stock-productstocklevel-data-table' style={{ flex: '1 1 100%' }}>
                <thead>
                    <tr>
                        <th>{t('ProductStockLevelTable:tableHeaderDate')}</th>
                        <th className='bordered center'>{t('ProductStockLevelTable:tableHeaderCurrentStock')}</th>
                        <th className='center'>{t('ProductStockLevelTable:tableHeaderPaidAllocatedStock')}</th>
                        <th className='center'>{t('ProductStockLevelTable:tableHeaderUnpaidAllocatedStock')}</th>
                        <th className='center'>{t('ProductStockLevelTable:tableHeaderAvailableStock')}</th>
                        <th className='center'>{t('ProductStockLevelTable:tableHeaderStatus')}</th>
                    </tr>
                </thead>
                <tbody>
                    {enhancedData.map((day, index) => (
                        <tr key={index} className={day.date.isSame(moment(), 'day') ? 'info' : undefined}>
                            <td>{formatMoment(moment(day.date), dateFormat)}</td>
                            <td className='bordered center'>{day.currentStock.toLocaleString()}</td>
                            <td className='bordered center'><span>{day.paidAllocatedStock > 0 ? '+' : ''}{day.paidAllocatedStock.toLocaleString()}</span></td>
                            <td className='bordered center'><span>{day.unpaidAllocatedStock > 0 ? '+' : ''}{day.unpaidAllocatedStock.toLocaleString()}</span></td>
                            <td className='bordered center'>{day.availableStock.toLocaleString()}</td>
                            <td className='bordered center'>
                                {day.stockWarnings.map((msg, i) => <div key={i}><span className='glyphicon glyphicon-alert' style={warningIconStyle}></span><span>{msg}</span></div>)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

ProductStockLevelTable.contextTypes = {
    t: PropTypes.func
}

export default ProductStockLevelTable;
